import { useCallback, useMemo, type RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import { pathForTarget, type NavTargetKey } from '../../services/assistant/navTargets';
import type { ChatFn } from '../../services/assistant/engine';
import { useUserStore } from '../../stores/userStore';
import { ChatComposer, ChatThread, type ChatComposerHandle } from '@reloam/ui';
import { MessageRow, TypingIndicator } from './AssistantMessages';
import AssistantWelcome, { PromptChips, SUGGESTED_PROMPTS } from './AssistantWelcome';
import { useAssistant, usePortfolioSnapshot } from './useAssistant';

interface AssistantThreadProps {
  composerRef: RefObject<ChatComposerHandle>;
  /** Called after a nav action moved the app to another page (phones close the panel). */
  onNavigated: () => void;
  /** Injectable for tests; defaults to the backend /ai/chat endpoint. */
  chat?: ChatFn;
}

const MAX_FOLLOW_UPS = 3;

/**
 * The AI assistant conversation: welcome with live stats and suggested
 * prompts, grounded replies with page links, follow-up chips. Rendered inside
 * the shared chat panel, which provides the header and conversation switcher.
 */
export default function AssistantThread({ composerRef, onNavigated, chat }: AssistantThreadProps) {
  const navigate = useNavigate();
  const firstName = useUserStore((s) => s.currentUser?.contact?.first_name || undefined);
  const { messages, awaiting, send } = useAssistant(chat);
  const snapshot = usePortfolioSnapshot();

  const handleNavigate = useCallback(
    (target: NavTargetKey) => {
      navigate(pathForTarget(target));
      onNavigated();
    },
    [navigate, onNavigated],
  );

  const handleSend = useCallback(
    (text: string) => {
      void send(text);
      composerRef.current?.focus();
    },
    [send, composerRef],
  );

  const followUps = useMemo(() => {
    const asked = new Set(messages.filter((m) => m.role === 'user').map((m) => m.content.trim().toLowerCase()));
    return SUGGESTED_PROMPTS.filter((p) => !asked.has(p.toLowerCase())).slice(0, MAX_FOLLOW_UPS);
  }, [messages]);

  const isEmpty = messages.length === 0;

  return (
    <ChatThread
      messageCount={isEmpty ? 0 : messages.length + (awaiting ? 1 : 0)}
      empty={<AssistantWelcome firstName={firstName} snapshot={snapshot} onPick={handleSend} />}
      composer={
        <>
          {!isEmpty && followUps.length > 0 && (
            <div className="mb-2">
              <PromptChips prompts={followUps} onPick={handleSend} disabled={awaiting} layout="scroll" />
            </div>
          )}
          <ChatComposer
            ref={composerRef}
            ariaLabel="Message the assistant"
            placeholder="Ask about plots, tenants, payments…"
            disabled={awaiting}
            onSend={handleSend}
          />
          <p className="mt-1.5 text-center text-[11px] text-bodydark1 dark:text-bodydark2">
            Figures come from your Re:Loam records. AI-written answers can be wrong.
          </p>
        </>
      }
    >
      {messages.map((m) => (
        <MessageRow key={m.id} message={m} onNavigate={handleNavigate} />
      ))}
      {awaiting && <TypingIndicator />}
    </ChatThread>
  );
}
